import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Cell } from "@/components/board/cells";
import { Icon } from "@/components/ui/Icon";
import { IconButton } from "@/components/ui/Button";
import { type PillStyle } from "@/components/ui/StatusPill";
import { BulkFieldMenu, type BulkEditableColumn } from "@/components/board/BulkFieldMenu";
import { isWorkflowStatusColumn } from "@/lib/statusRoles";
import { canDeleteGroup } from "@/lib/groupDelete";
import { ColumnMenu } from "@/components/board/ColumnMenu";
import {
  MoveSubitemMenu,
  type MoveTargetParent,
  type MoveTargetSection,
} from "@/components/board/MoveSubitemMenu";
import { RowContextMenu, type CopyRowOpts } from "@/components/board/RowCopyMenu";
import { nextChildPosition, nextTopLevelPosition } from "@/lib/moveTargets";
import { flattenGroupRows, projectDepth, resolveSubitemDrop } from "@/lib/rowDropTarget";
import { GoalDateControl } from "@/components/board/GoalDateControl";
import { AddColumnDialog } from "@/components/dialogs/AddColumnDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { DuplicateGroupDialog } from "@/components/dialogs/DuplicateGroupDialog";
import { BRAND, SCALE, statusColorOf } from "@/lib/tokens";
import { fmtCurrency } from "@/lib/format";
import { t } from "@/lib/strings";
import { columnIconName } from "@/lib/columnIcon";
import { deriveSectionYear, isTicketBoard } from "@/lib/boardView";
import type { ViewBoard, ViewColumn, ViewRow } from "@/lib/boardView";
import type {
  BoardKind,
  CellValue,
  ColumnConfig,
  ColumnDef,
  ColumnUpdatePayload,
  UUID,
} from "@/lib/types";

export type Density = "compact" | "comfy" | "spacious";

interface TableViewProps {
  board: ViewBoard;
  density: Density;
  pillStyle: PillStyle;
  /** Column id to group top-level rows by, or null for a flat list. */
  groupByColumnId: string | null;
  /** The resolved group-by column — passed from the full board so the table can
   * group by a column it's NOT rendering (e.g. a hidden Bereich column whose
   * value already shows in the section header). */
  groupColumn?: ViewColumn | null;
  /** Year-tab filter: show only sections of this year (null = all). */
  yearFilter?: number | null;
  onPatchRow: (id: string, patch: Partial<ViewRow>) => void;
  onRowClick: (row: ViewRow) => void;
  /** Inline-create. `inGroup` (B7) sets the grouping column's value so the new
   * row lands in the group it was added from instead of "Ohne Gruppe". */
  onAddRow?: (afterRowId?: UUID | null, inGroup?: { columnId: UUID; value: CellValue }) => void;
  /** Generic per-cell text write — plumbed through Cell for non-title text columns. */
  onPatchCell?: (rowId: string, columnDefId: string, value: CellValue) => void;
  /** Board-admin-only inline-create for status + tags column options. */
  onAddColumnOption?: (
    column: ColumnDef,
    label: string,
  ) => Promise<{ id: string; label: string; color: string } | null>;
  canAddColumnOption?: boolean;
  /** Show the "+ Spalte" add-column affordance. False for guests. */
  canAddColumn?: boolean;
  /** Drag-to-reorder writes: PATCH the moved row's position. */
  onRowPositionChange?: (rowId: UUID, position: number) => void;
  /** Reparent/outdent a sub-item: PATCH parent_row_id + position together.
   * null parent = promote to top-level (caller writes the group-by cell separately). */
  onMoveRow?: (rowId: UUID, parentRowId: UUID | null, position: number) => void;
  /** Drag-to-reorder + resize writes for columns. */
  onColumnUpdate?: (columnId: UUID, payload: ColumnUpdatePayload) => void;
  /** Sub-items: mint a new row as a child of the given parent. */
  onAddSubitem?: (parentRowId: UUID) => void;
  /** Soft-delete a row (undoable). Wires the bulk-selection Delete action. */
  onDeleteRow?: (rowId: UUID) => void;
  onCopyRow?: (rowId: UUID, opts: CopyRowOpts) => void;
  /** Delete a whole status group (the caller confirms, then deletes its rows + archives
   *  the option). Absent → no delete control (e.g. guests). */
  onDeleteGroup?: (groupColumnId: string, option: { id: string; label: string }) => void;
  onDuplicateGroup?: (
    groupColumnId: string,
    sourceValue: unknown,
    newLabel: string,
    withComments: boolean,
  ) => void;
  /** A just-created row to auto-focus (cursor in its title) + scroll into view.
   * Its group is auto-expanded so it's never hidden. */
  focusRowId?: string | null;
  /** Called once the focusRowId has been consumed (so it doesn't re-fire). */
  onFocusConsumed?: () => void;
  /** Row whose detail panel is currently open — persistently highlighted so a
   * bell-notification deep-link makes clear which row it opened. */
  openRowId?: string | null;
}

interface RowGroup {
  option: { id: string; label: string; color: string } | null;
  rows: ViewRow[];
}

// Row gutter geometry. The gutter holds fixed-size controls: expand chevron (a row with
// sub-items), drag grip, selection checkbox and "+ Untereintrag" (a sub-item has its move
// menu instead of the "+"). 🔴 The controls never shrink and the gutter is sized to FIT
// them. It used to be a fixed 64px that a category row's four controls (74px) overflowed,
// and flex-shrink silently collapsed the one control with no content — the unchecked
// selection checkbox — to 2px, so a row with sub-items could no longer be selected or
// bulk-deleted. Adding a gutter control means widening these, never squeezing.
const GUTTER_PAD = 6;
const GUTTER_GAP = 4;
/** The gutter's 1px right border sits inside its (border-box) width. */
const GUTTER_BORDER = 1;
/** grip + checkbox + "+" (or grip + move menu + checkbox): 3 × 16px + 2 gaps + padding + border. */
const GUTTER_W = 3 * 16 + 2 * GUTTER_GAP + 2 * GUTTER_PAD + GUTTER_BORDER;
/** …plus the 14px expand chevron, once any row on the board has sub-items. */
const GUTTER_W_TREE = GUTTER_W + 14 + GUTTER_GAP;

/** Render a cell value as a group label for generic (non-status) grouping. */
function formatGroupLabel(v: unknown): string {
  if (v == null) return "";
  if (Array.isArray(v)) return v.map(String).join(", ");
  if (typeof v === "boolean") return v ? "Ja" : "Nein";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function TableView({
  board,
  density,
  pillStyle,
  groupByColumnId,
  groupColumn = null,
  yearFilter = null,
  onPatchRow,
  onRowClick,
  onAddRow,
  onPatchCell,
  onAddColumnOption,
  canAddColumnOption = false,
  canAddColumn = true,
  onRowPositionChange,
  onColumnUpdate,
  onAddSubitem,
  onMoveRow,
  onDeleteRow,
  onCopyRow,
  onDuplicateGroup,
  onDeleteGroup,
  focusRowId = null,
  onFocusConsumed,
  openRowId = null,
}: TableViewProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require an 8px drag before activating — otherwise clicks on headers /
      // cells / menus would constantly start drags.
      activationConstraint: { distance: 8 },
    }),
  );

  function handleColumnDragEnd(e: DragEndEvent) {
    if (!onColumnUpdate) return;
    const activeId = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (!overId || activeId === overId) return;
    const ids = board.columns.map((c) => c.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;
    const reordered = arrayMove(board.columns, from, to);
    // Midpoint between new neighbours; fall back to ±1000 at the ends.
    const prevPos = reordered[to - 1]?.raw?.position ?? null;
    const nextPos = reordered[to + 1]?.raw?.position ?? null;
    let newPos: number;
    if (prevPos === null && nextPos !== null) newPos = nextPos - 1000;
    else if (prevPos !== null && nextPos === null) newPos = prevPos + 1000;
    else if (prevPos !== null && nextPos !== null) newPos = (prevPos + nextPos) / 2;
    else newPos = reordered[to]?.raw?.position ?? 0;
    onColumnUpdate(activeId as UUID, { position: newPos });
  }

  // Reorder a row within its group (decimal-position halving between neighbours).
  function reorderRowWithin(rowsInGroup: ViewRow[], activeId: string, overId: string) {
    if (!onRowPositionChange) return;
    const ids = rowsInGroup.map((r) => r.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;
    const reordered = arrayMove(rowsInGroup, from, to);
    const prevPos = reordered[to - 1]?.raw?.position ?? null;
    const nextPos = reordered[to + 1]?.raw?.position ?? null;
    let newPos: number;
    if (prevPos === null && nextPos !== null) newPos = nextPos - 1000;
    else if (prevPos !== null && nextPos === null) newPos = prevPos + 1000;
    else if (prevPos !== null && nextPos !== null) newPos = (prevPos + nextPos) / 2;
    else newPos = reordered[to]?.raw?.position ?? 0;
    onRowPositionChange(activeId as UUID, newPos);
  }

  // Single onDragEnd for the unified context: a group-header drag (active id is a
  // group option) reorders groups; a row drag reorders within its group, or — when
  // dropped on another group's row/header — MOVES the row there (F5) by writing the
  // grouping column's value. Sub-items aren't draggable, so only top-level rows hit
  // this. Each branch self-guards, so the two never cross-fire.
  function handleBoardDragEnd(e: DragEndEvent) {
    // Capture the live-reparent target BEFORE clearing it — with the cross-group
    // preview the dragged row is rendered in the destination group, so dnd-kit's
    // `over` can resolve to the dragged row itself; `dragOver` is the reliable
    // source of truth for where the user actually let go.
    const pending = dragOver;
    setActiveDragId(null);
    setDragOver(null);
    setSubitemOverId(null);
    const activeId = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (groupItems.includes(activeId)) {
      handleGroupDragEnd(e); // group-header reorder (no-ops unless both are groups)
      return;
    }

    // Sub-item branch (Task 5): a WITHIN-GROUP move — reorder among siblings,
    // reparent under another parent in the same section, or promote to top-level
    // in the same section. Resolved by the pure `resolveSubitemDrop`, then
    // returns BEFORE the top-level F5 logic so that path is untouched. (Cross-group
    // sub-item drags are Task 6.)
    const activeRow = board.rows.find((r) => r.id === activeId);
    if (activeRow && activeRow.depth > 0) {
      const depthForDrop = projectedDepth;
      setProjectedDepth(1);
      const grp = grouped.find((g) => g.rows.some((r) => r.id === activeRow.parentRowId));
      // Task 6: a genuine CROSS-group drop — the live preview target (`pending`,
      // captured at the top) points at a DIFFERENT section than the sub-item's
      // current parent group. Handle it BEFORE the within-group resolver so that
      // Task-5 path stays untouched. Depth 1 dropped onto a specific top-level
      // parent row in the destination → reparent under it (auto-expand it);
      // otherwise promote into that section with F5's grouping-value encoding.
      if (pending && pending.groupId !== grp?.option?.id) {
        const dst = grouped.find((g) => g.option?.id === pending.groupId);
        if (dst) {
          const overIsParent = dst.rows.find((r) => r.id === pending.overId);
          if (depthForDrop === 1 && overIsParent && onMoveRow) {
            onMoveRow(
              activeId as UUID,
              overIsParent.id as UUID,
              nextChildPosition(posRows, overIsParent.id),
            );
            if (!expanded.has(overIsParent.id)) toggleExpand(overIsParent.id);
          } else {
            const topIds = dst.rows.map((r) => r.id);
            if (onMoveRow) onMoveRow(activeId as UUID, null, nextTopLevelPosition(posRows, topIds));
            if (dst.option && groupCol && onPatchCell) {
              const value: CellValue =
                dst.option.id === "__none__"
                  ? ((groupCol.type === "tags" ? [] : "") as CellValue)
                  : ((groupCol.type === "status" ? dst.option.id : dst.option.label) as CellValue);
              onPatchCell(activeId, groupCol.id, value);
            }
          }
          return;
        }
      }
      const flat = flattenGroupRows(
        (grp?.rows ?? []).map((r) => ({
          id: r.id,
          position: r.raw?.position ?? 0,
          expanded: expanded.has(r.id) && r.childCount > 0,
          children: (childrenByParent.get(r.id) ?? []).map((c) => ({
            id: c.id,
            position: c.raw?.position ?? 0,
          })),
        })),
      );
      const target = resolveSubitemDrop({
        rows: flat,
        activeId,
        overId: overId ?? activeId,
        projectedDepth: depthForDrop,
      });
      if (!target) return;
      if (target.parentRowId === null) {
        // Promote to top-level, landing in this group's section (F5 value encoding).
        if (onMoveRow) onMoveRow(activeId as UUID, null, target.position);
        if (grp?.option && groupCol && onPatchCell) {
          const value: CellValue =
            grp.option.id === "__none__"
              ? ((groupCol.type === "tags" ? [] : "") as CellValue)
              : ((groupCol.type === "status" ? grp.option.id : grp.option.label) as CellValue);
          onPatchCell(activeId, groupCol.id, value);
        }
      } else if (onMoveRow) {
        onMoveRow(activeId as UUID, target.parentRowId as UUID, target.position);
        // Auto-expand the target parent so the moved sub-item is visible after drop.
        if (!expanded.has(target.parentRowId)) toggleExpand(target.parentRowId);
      }
      return;
    }

    const src = grouped.find((g) => g.rows.some((r) => r.id === activeId));
    if (!src) return;

    // Resolve the destination group + the row we dropped onto. Prefer the live
    // preview (a cross-group hover); otherwise read it off the raw `over`.
    let dst: RowGroup | undefined;
    let overRowId: string | undefined;
    if (pending) {
      dst = grouped.find((g) => g.option?.id === pending.groupId);
      overRowId = pending.overId;
    }
    if (!dst && overId) {
      // Children are now sortable too (Task 5), so a top-level drag can resolve
      // `over` to a child row. Map that to its top-level parent so the plain
      // reorder still lands correctly — top-level-over-top-level is unchanged
      // (parentRowId is null → topOverId === overId).
      const overRow = board.rows.find((r) => r.id === overId);
      const topOverId = overRow?.parentRowId ?? overId;
      dst = grouped.find((g) => g.rows.some((r) => r.id === topOverId));
      if (!dst) dst = grouped.find((g) => g.option?.id === topOverId); // dropped on a header
      overRowId = topOverId;
    }
    if (!dst) return;

    if (src.option?.id === dst.option?.id) {
      // Same group: a plain reorder (skip the no-op self-drop).
      if (overRowId && overRowId !== activeId) reorderRowWithin(src.rows, activeId, overRowId);
      return;
    }
    // Cross-group move (F5): set the grouping column's value AND a position at the
    // dropped slot, so the row lands exactly where the live-reparent preview showed
    // it (no post-drop jump).
    if (!groupCol || !dst.option || !onPatchCell) return;
    const value: CellValue =
      dst.option.id === "__none__"
        ? ((groupCol.type === "tags" ? [] : "") as CellValue)
        : ((groupCol.type === "status" ? dst.option.id : dst.option.label) as CellValue);
    onPatchCell(activeId, groupCol.id, value);
    if (onRowPositionChange) {
      const dstRows = dst.rows.filter((r) => r.id !== activeId);
      const overIdx = dstRows.findIndex((r) => r.id === overRowId);
      const prev = overIdx > 0 ? (dstRows[overIdx - 1]?.raw?.position ?? null) : null;
      const cur = overIdx >= 0 ? (dstRows[overIdx]?.raw?.position ?? null) : null;
      const last = dstRows[dstRows.length - 1]?.raw?.position ?? 0;
      let newPos: number;
      if (cur != null && prev != null) newPos = (prev + cur) / 2;
      else if (cur != null) newPos = cur - 1000;
      else newPos = last + 1000;
      onRowPositionChange(activeId as UUID, newPos);
    }
  }

  // Live cross-group reparent preview: as the dragged row hovers over a DIFFERENT
  // group, record that group + slot so the render moves the row into it (rows make
  // room). Stability is load-bearing here — once the row is previewed into the
  // target it sits UNDER the pointer, so dnd-kit reports `over === active`. We must
  // NOT re-resolve that against the real (un-previewed) layout: it would read the
  // row's ORIGINAL group, flip the preview back to the source (jittering that
  // group's rows), then re-enter the target next frame — an oscillation that at
  // worst trips React #185 (max update depth). So: ignore `over === active`, and
  // only ever write a genuinely-new target (idempotent — no churn re-renders).
  function handleBoardDragOver(e: DragOverEvent) {
    const activeId = String(e.active.id);
    if (groupItems.includes(activeId)) return; // group-header drag — no row reparent
    const overId = e.over?.id != null ? String(e.over.id) : null;
    // Sub-item drags (Task 5) reorder NATIVELY inside the SortableContext
    // (make-room). They must NEVER engage the cross-group reparent preview below:
    // that path's `over === active` handling is sound only for top-level rows, and
    // driving it from a sub-item would risk the exact #185 oscillation it guards
    // against. Track the hovered slot solely to paint the drop-indicator line.
    const activeRow = board.rows.find((r) => r.id === activeId);
    if (activeRow && activeRow.depth > 0) {
      // Task 5: paint the make-room drop-indicator line for the WITHIN-group
      // native reorder.
      setSubitemOverId((prev) => (prev === overId ? prev : overId));
      // Task 6: ALSO record a cross-group target so a drop into a DIFFERENT
      // section can promote/reparent (consumed in handleBoardDragEnd). This is
      // safe from the #185 oscillation the top-level path guards against: the
      // visual preview (displayGrouped) only relocates a row it finds as a
      // TOP-LEVEL `src` entry, which a sub-item never is, so the sub-item is
      // never rendered under the pointer and `over` stays a stable destination
      // row. We still ignore `over === active` and write idempotently, exactly
      // like the top-level path — so `over === active` is never re-resolved.
      if (!overId || overId === activeId) return;
      let g = grouped.find((gr) => gr.rows.some((r) => r.id === overId));
      if (!g) g = grouped.find((gr) => gr.option?.id === overId);
      const srcGroup = grouped.find((gr) => gr.rows.some((r) => r.id === activeRow.parentRowId));
      if (!g || !g.option || (srcGroup && srcGroup.option?.id === g.option.id)) {
        setDragOver((prev) => (prev === null ? prev : null)); // same/own section → no cross-group target
        return;
      }
      const groupId = g.option.id;
      setDragOver((prev) =>
        prev && prev.groupId === groupId && prev.overId === overId ? prev : { groupId, overId },
      );
      return;
    }
    // Nothing under the pointer, or the pointer is over the dragged row's own
    // preview slot → keep whatever preview we already have (don't recompute).
    if (!overId || overId === activeId) return;
    let g = grouped.find((gr) => gr.rows.some((r) => r.id === overId));
    if (!g) g = grouped.find((gr) => gr.option?.id === overId);
    const src = grouped.find((gr) => gr.rows.some((r) => r.id === activeId));
    if (!g || !g.option || (src && src.option?.id === g.option.id)) {
      setDragOver((prev) => (prev === null ? prev : null)); // back over source → drop preview
      return;
    }
    const groupId = g.option.id;
    setDragOver((prev) =>
      prev && prev.groupId === groupId && prev.overId === overId ? prev : { groupId, overId },
    );
  }

  // Reorder groups = reorder the status group-by column's options (no position
  // field; it's an ordered array). Persists via the existing column-config PATCH.
  function handleGroupDragEnd(e: DragEndEvent) {
    if (!onColumnUpdate || !groupCol?.raw || groupCol.type !== "status") return;
    const activeId = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (!overId || activeId === overId) return;
    if (activeId === "__none__" || overId === "__none__") return;
    const cfg = (groupCol.raw.config ?? {}) as ColumnConfig;
    const opts = cfg.options ?? [];
    // Map back to indices in the FULL options array (the rendered groups can be
    // a filtered subset — empty/year-filtered — so don't index into `grouped`).
    const from = opts.findIndex((o) => o.id === activeId);
    const to = opts.findIndex((o) => o.id === overId);
    if (from < 0 || to < 0) return;
    const options = arrayMove(opts, from, to);
    onColumnUpdate(groupCol.raw.id as UUID, { config: { ...cfg, options } });
  }

  const rowH = density === "compact" ? 34 : density === "spacious" ? 52 : 42;

  const groupCol = useMemo(
    () =>
      groupColumn ??
      (groupByColumnId ? (board.columns.find((c) => c.id === groupByColumnId) ?? null) : null),
    [groupColumn, board.columns, groupByColumnId],
  );

  // Vorlaufzeit: the goal date for a row = the Zieltermin of its section (the
  // grouping column's option), falling back to the board-level goal date.
  const resolveRowGoalDate = (row: ViewRow): string | null => {
    if (groupCol && groupCol.type === "status") {
      const valueFor = (r: ViewRow) =>
        r.raw?.cells?.find((c) => c.column_def_id === groupCol.id)?.value;
      let v = valueFor(row);
      // Sub-items carry no section value of their own — inherit the parent's.
      if (typeof v !== "string" && row.parentRowId) {
        const parent = board.rows.find((r) => r.id === row.parentRowId);
        if (parent) v = valueFor(parent);
      }
      if (typeof v === "string") {
        const d = groupCol.options?.find((o) => o.id === v)?.goal_date;
        if (d) return d;
      }
    }
    return board.goalDate;
  };

  // Persist a section's Zieltermin onto the grouping column's matching option
  // (free-form JSONB, like status roles — no schema change).
  function setSectionGoalDate(optionId: string, date: string | null) {
    if (!onColumnUpdate || !groupCol?.raw) return;
    const cfg = (groupCol.raw.config ?? {}) as ColumnConfig;
    const options = (cfg.options ?? []).map((o) =>
      o.id === optionId ? { ...o, goal_date: date ?? undefined } : o,
    );
    onColumnUpdate(groupCol.raw.id as UUID, { config: { ...cfg, options } });
  }

  // Rename a section by renaming its option's label on the grouping column. The
  // option id + all cell values are unchanged, so every row in the section
  // follows automatically.
  function renameSection(optionId: string, label: string) {
    if (!onColumnUpdate || !groupCol?.raw) return;
    const trimmed = label.trim();
    if (!trimmed) return;
    const cfg = (groupCol.raw.config ?? {}) as ColumnConfig;
    const options = (cfg.options ?? []).map((o) =>
      o.id === optionId ? { ...o, label: trimmed } : o,
    );
    onColumnUpdate(groupCol.raw.id as UUID, { config: { ...cfg, options } });
  }

  const grouped = useMemo<RowGroup[]>(() => {
    // Always partition only TOP-LEVEL rows; sub-items stay nested under their
    // parent within each section (subtasks have no group of their own, so
    // flattening them would orphan them into an "Ohne …" bucket).
    const topLevel = board.rows.filter((r) => !r.parentRowId);
    if (!groupCol) return [{ option: null, rows: topLevel }];

    const valueOf = (r: ViewRow): unknown =>
      r.raw?.cells?.find((c) => c.column_def_id === groupCol.id)?.value ?? null;

    // Status/tags-with-options: section per option, in the column's own order,
    // plus a trailing bucket for rows without a (valid) value.
    if (groupCol.type === "status" && groupCol.options?.length) {
      const groups: RowGroup[] = groupCol.options.map((opt) => ({
        option: { id: opt.id, label: opt.label, color: opt.color as string },
        rows: topLevel.filter((r) => valueOf(r) === opt.id),
      }));
      const valid = new Set(groupCol.options.map((o) => o.id));
      const none = topLevel.filter((r) => {
        const v = valueOf(r);
        return typeof v !== "string" || !valid.has(v);
      });
      if (none.length) {
        groups.push({
          option: { id: "__none__", label: `Ohne ${groupCol.name}`, color: "#cbd5e1" },
          rows: none,
        });
      }
      let visible = groups.filter((g) => g.rows.length > 0);
      // Year-tabs: keep only sections whose year matches the selected one.
      if (yearFilter != null) {
        visible = visible.filter((g) => {
          const opt = groupCol.options?.find((o) => o.id === g.option!.id);
          return deriveSectionYear(g.option!.label, opt?.goal_date) === yearFilter;
        });
      }
      return visible;
    }

    // Generic value grouping (tags, text, checkbox), in first-appearance order.
    const buckets = new Map<string, RowGroup>();
    for (const r of topLevel) {
      const label = formatGroupLabel(valueOf(r)) || `Ohne ${groupCol.name}`;
      let bucket = buckets.get(label);
      if (!bucket) {
        bucket = { option: { id: label, label, color: "#cbd5e1" }, rows: [] };
        buckets.set(label, bucket);
      }
      bucket.rows.push(r);
    }
    return Array.from(buckets.values());
  }, [board.rows, groupCol, yearFilter]);

  // Group collapse state, persisted per-board in localStorage (B3): without this
  // it was in-memory only and reset to all-expanded on every remount/refetch.
  const collapseKey = `flow.groups.collapsed.${board.id}`;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const raw = window.localStorage.getItem(collapseKey);
      if (!raw) return {};
      const obj = JSON.parse(raw);
      return obj && typeof obj === "object" ? (obj as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });
  function toggleCollapsed(optionId: string) {
    setCollapsed((prev) => {
      const next = { ...prev, [optionId]: !prev[optionId] };
      try {
        window.localStorage.setItem(collapseKey, JSON.stringify(next));
      } catch {
        /* localStorage may be unavailable */
      }
      return next;
    });
  }
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // F8: the last-clicked row, anchor for shift-click range selection.
  const selectAnchorRef = useRef<string | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [hoverRow, setHoverRow] = useState<string | null>(null);
  // Row currently being dragged — drives the DragOverlay, which gives the drop a
  // smooth "fly into place" animation, incl. when moved ACROSS groups.
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  // While dragging across groups, which group + slot the row is hovered over. The
  // render reparents the dragged row into that group LIVE so its rows shift to make
  // room (the kanban cross-container animation), matching within-group reorder.
  const [dragOver, setDragOver] = useState<{ groupId: string; overId: string } | null>(null);
  // Task 5 (sub-item drag): binary projected depth from the drag's horizontal
  // delta (0 = promote to top-level, 1 = stay nested) + the hovered slot, used
  // only to paint the thin drop-indicator line. The make-room animation itself is
  // native to the SortableContext — these never drive the cross-group `dragOver`
  // preview (see the #185 note on handleBoardDragOver).
  const [projectedDepth, setProjectedDepth] = useState<0 | 1>(1);
  const [subitemOverId, setSubitemOverId] = useState<string | null>(null);
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);
  const [addColOpen, setAddColOpen] = useState(false);
  // Duplicate-a-whole-group dialog: which group is being cloned + its source value.
  const [groupDup, setGroupDup] = useState<{ sourceValue: unknown; label: string } | null>(null);

  // Sub-items expand/collapse state, persisted per-board in localStorage so
  // navigating away + back keeps the tree in the user's preferred shape.
  const expandKey = `flow.tree.expanded.${board.id}`;
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const raw = window.localStorage.getItem(expandKey);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? new Set(arr.filter((x) => typeof x === "string")) : new Set();
    } catch {
      return new Set();
    }
  });
  function toggleExpand(rowId: string) {
    // Clear any pinned hover: collapsing unmounts the child rows, and the parent's
    // onMouseLeave can be lost across that DOM change — leaving the row stuck in its
    // hover background (a lighter grey in dark mode) even after the cursor moves away.
    setHoverRow(null);
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(rowId)) n.delete(rowId);
      else n.add(rowId);
      try {
        window.localStorage.setItem(expandKey, JSON.stringify(Array.from(n)));
      } catch {
        /* localStorage may be unavailable */
      }
      return n;
    });
  }

  // Index sub-items by parent for O(1) child lookup during render.
  const childrenByParent = useMemo<Map<string, ViewRow[]>>(() => {
    const map = new Map<string, ViewRow[]>();
    for (const r of board.rows) {
      if (r.parentRowId) {
        const existing = map.get(r.parentRowId);
        if (existing) existing.push(r);
        else map.set(r.parentRowId, [r]);
      }
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.raw?.position ?? 0) - (b.raw?.position ?? 0));
    }
    return map;
  }, [board.rows]);

  // Move-sub-item menu fallback (Task 4): the target lists + position math live
  // here, where `grouped`/`groupCol`/`board.rows` are in scope. ViewRow drops the
  // raw `position`, so rebuild the minimal {id, parentRowId, position} shape the
  // pure moveTargets helper needs (no `as never` — the repo bans it).
  const posRows = useMemo(
    () =>
      board.rows.map((r) => ({
        id: r.id,
        parentRowId: r.parentRowId,
        position: r.raw?.position ?? 0,
      })),
    [board.rows],
  );
  // All top-level entries (children live in childrenByParent, not in grouped).
  const moveParents = useMemo<MoveTargetParent[]>(
    () => grouped.flatMap((g) => g.rows.map((r) => ({ id: r.id, title: r.title }))),
    [grouped],
  );
  const topLevelIds = useMemo(() => moveParents.map((p) => p.id), [moveParents]);
  // Promote targets = the current group headers. Encode each header's cell value
  // exactly like F5's group-drop: status → option.id, else the label; the
  // "Ohne <Spalte>" bucket clears the cell (null). null when the board has no
  // group-by (menu then offers a single "top level" promote).
  const moveSections = useMemo<MoveTargetSection[] | null>(() => {
    if (!groupCol) return null;
    return grouped.map((g) => {
      const opt = g.option;
      const isNone = !opt || opt.id === "__none__";
      const value: unknown = isNone ? null : groupCol.type === "status" ? opt.id : opt.label;
      return { value, label: opt?.label ?? `Ohne ${groupCol.name}` };
    });
  }, [grouped, groupCol]);

  // Flat ordered list of visible row ids (across all expanded groups + tree
  // expansions) — used by keyboard nav so j/k / arrow keys move sensibly
  // even when groupByStatus partitions rows or sub-items are expanded.
  const flatRowIds = useMemo<string[]>(() => {
    const out: string[] = [];
    for (const g of grouped) {
      const isCollapsedGroup = g.option ? Boolean(collapsed[g.option.id]) : false;
      if (isCollapsedGroup) continue;
      for (const r of g.rows) {
        out.push(r.id);
        // Sub-items stay nested under their parent in every mode.
        if (expanded.has(r.id)) {
          const kids = childrenByParent.get(r.id) ?? [];
          for (const k of kids) out.push(k.id);
        }
      }
    }
    return out;
  }, [grouped, collapsed, expanded, childrenByParent]);

  // Header select-all reflects the VISIBLE rows — exactly flatRowIds, so it respects
  // collapsed groups + expanded sub-items. Some-but-not-all selected -> indeterminate.
  const allVisibleSelected = flatRowIds.length > 0 && flatRowIds.every((id) => selected.has(id));
  const someVisibleSelected = !allVisibleSelected && flatRowIds.some((id) => selected.has(id));

  // A just-created row (focusRowId) must be visible so its auto-focused title
  // cell + cursor land in view: expand its parent (if a sub-item) and its group
  // (if collapsed), then release the focus signal once it has rendered.
  useEffect(() => {
    if (!focusRowId) return;
    const target = board.rows.find((r) => r.id === focusRowId);
    if (!target) return; // not in the cache yet — re-runs when board.rows updates
    const parentId = target.raw?.parent_row_id;
    if (parentId && !expanded.has(parentId)) toggleExpand(parentId);
    for (const g of grouped) {
      if (g.option && collapsed[g.option.id] && g.rows.some((r) => r.id === focusRowId)) {
        toggleCollapsed(g.option.id);
      }
    }
    const id = window.setTimeout(() => onFocusConsumed?.(), 1000);
    return () => window.clearTimeout(id);
    // toggleExpand/toggleCollapsed are stable enough; re-running on collapsed/
    // expanded changes is guarded by the `if` conditions (no loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRowId, board.rows, grouped]);

  // Deep-link / bell: the OPENED row (openRowId) already gets the 3px accent bar,
  // but nothing scrolled it into view — so a deep-linked ticket below the fold was
  // opened but off-screen. Scroll it into view ONCE per openRowId value (a ref
  // guard, so re-renders / a manual reopen of the same row don't keep yanking the
  // scroll). The row draws its own DOM id (`board-row-<id>`). Fires when the row is
  // rendered; if it's inside a collapsed group it simply doesn't scroll (the panel
  // still opens) — acceptable, like the comment-flash edge.
  const scrolledOpenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openRowId) {
      scrolledOpenRef.current = null;
      return;
    }
    if (scrolledOpenRef.current === openRowId) return;
    const el = document.getElementById(`board-row-${openRowId}`);
    if (!el) return; // not rendered yet (or collapsed) — retry on the next render
    scrolledOpenRef.current = openRowId;
    el.scrollIntoView({ block: "center" });
  }, [openRowId, board.rows]);

  // Bulk-edit fan-out: when 2+ rows are selected AND the user edits a cell on
  // one of them, the patch is broadcast to every selected row. Single-row
  // selection or unselected edits stay normal. Backend processes N parallel
  // upserts — atomicity is best-effort (v1 trade-off; batch endpoint deferred).
  // Exception: `title` is always row-unique — mass-renaming every selected
  // row to the same title would never be what the user wants.
  const bulkOnPatchRow = useMemo(
    () => (id: string, patch: Partial<ViewRow>) => {
      const fanOut = selected.has(id) && selected.size > 1 && !("title" in patch);
      if (fanOut) {
        for (const rid of selected) onPatchRow(rid, patch);
      } else {
        onPatchRow(id, patch);
      }
    },
    [onPatchRow, selected],
  );
  const bulkOnPatchCell = useMemo(
    () =>
      onPatchCell
        ? (rowId: string, columnDefId: string, value: CellValue) => {
            if (selected.has(rowId) && selected.size > 1) {
              for (const rid of selected) onPatchCell(rid, columnDefId, value);
            } else {
              onPatchCell(rowId, columnDefId, value);
            }
          }
        : undefined,
    [onPatchCell, selected],
  );

  // Bulk-set a field across the whole selection (Status · Verantwortlich · Fällig ·
  // Vorlaufzeit · Schlagwörter). Column-scoped writes (not the field-based fan-out)
  // so a board with several columns of one type targets the RIGHT one, mirroring
  // how status bulk already worked. The workflow Status on a ticket board stays
  // action-only (excluded); status/tags need options to pick from.
  const bulkEditableColumns = useMemo<BulkEditableColumn[]>(() => {
    if (!onPatchCell) return [];
    const relevant = new Set(["status", "person", "date", "lead_time", "tags"]);
    return board.columns
      .filter((c) => relevant.has(c.type))
      .filter((c) => !(c.type === "status" && isTicketBoard(board) && isWorkflowStatusColumn(c)))
      .filter((c) =>
        c.type === "status" || c.type === "tags" ? (c.options?.length ?? 0) > 0 : true,
      )
      .map((c) => ({
        id: c.id,
        name: c.name,
        type: c.type,
        ...(c.options ? { options: c.options } : {}),
      }));
  }, [board, onPatchCell]);
  const onBulkSet = useMemo(
    () =>
      onPatchCell
        ? (columnId: string, value: CellValue) => {
            for (const rid of selected) onPatchCell(rid, columnId, value);
            setSelected(new Set());
          }
        : undefined,
    [onPatchCell, selected],
  );

  function moveFocus(delta: 1 | -1) {
    if (flatRowIds.length === 0) return;
    const idx = focusedRowId ? flatRowIds.indexOf(focusedRowId) : -1;
    if (idx < 0) {
      const seed = flatRowIds[delta > 0 ? 0 : flatRowIds.length - 1];
      if (seed) setFocusedRowId(seed);
      return;
    }
    const nextIdx = Math.max(0, Math.min(flatRowIds.length - 1, idx + delta));
    const next = flatRowIds[nextIdx];
    if (next) setFocusedRowId(next);
  }

  function toggleSelectId(id: string) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  // F8: shift-click a row's checkbox → select the whole contiguous range from the
  // last-clicked anchor to here (across sub-items + sections, in visible order).
  // A plain click toggles and re-anchors.
  function handleSelectClick(id: string, shiftKey: boolean) {
    const anchor = selectAnchorRef.current;
    if (shiftKey && anchor) {
      const a = flatRowIds.indexOf(anchor);
      const b = flatRowIds.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        const range = flatRowIds.slice(lo, hi + 1);
        setSelected((prev) => new Set([...prev, ...range]));
        return;
      }
    }
    selectAnchorRef.current = id;
    toggleSelectId(id);
  }

  function extendSelectionTo(delta: 1 | -1) {
    if (flatRowIds.length === 0) return;
    const idx = focusedRowId ? flatRowIds.indexOf(focusedRowId) : -1;
    if (idx < 0) {
      moveFocus(delta);
      return;
    }
    const nextIdx = Math.max(0, Math.min(flatRowIds.length - 1, idx + delta));
    const currentId = flatRowIds[idx];
    const nextId = flatRowIds[nextIdx];
    if (!currentId || !nextId) return;
    setFocusedRowId(nextId);
    setSelected((prev) => {
      const n = new Set(prev);
      n.add(currentId);
      n.add(nextId);
      return n;
    });
  }

  function onRootKeyDown(e: React.KeyboardEvent) {
    // Never hijack keys while the user is in an input / textarea / contentEditable.
    const tgt = e.target as HTMLElement;
    const tag = tgt.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tgt.isContentEditable) {
      return;
    }
    // Esc clears focus + selection.
    if (e.key === "Escape") {
      if (focusedRowId || selected.size > 0) {
        setFocusedRowId(null);
        setSelected(new Set());
        e.preventDefault();
      }
      return;
    }
    // Cmd/Ctrl+A selects all visible rows.
    if ((e.metaKey || e.ctrlKey) && (e.key === "a" || e.key === "A")) {
      e.preventDefault();
      setSelected(new Set(flatRowIds));
      return;
    }
    // Row navigation.
    if (e.key === "ArrowDown" || e.key === "j" || e.key === "J") {
      if (e.shiftKey) extendSelectionTo(1);
      else moveFocus(1);
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowUp" || e.key === "k" || e.key === "K") {
      if (e.shiftKey) extendSelectionTo(-1);
      else moveFocus(-1);
      e.preventDefault();
      return;
    }
    // X toggles selection on the focused row.
    if (e.key === "x" || e.key === "X") {
      if (focusedRowId) {
        toggleSelectId(focusedRowId);
        e.preventDefault();
      }
      return;
    }
    // Space + Enter open row detail (peek) — same effect for v1; full
    // Linear-style "hold Space to peek, release to dismiss" is deferred.
    if ((e.key === " " || e.key === "Enter") && focusedRowId) {
      const row = board.rows.find((r) => r.id === focusedRowId);
      if (row) {
        onRowClick(row);
        e.preventDefault();
      }
      return;
    }
  }

  // Wide gutter only when a chevron is actually drawn somewhere (childCount is counted
  // over the unfiltered board, so it matches what the row gutters render).
  const gutterW = board.rows.some((r) => r.childCount > 0) ? GUTTER_W_TREE : GUTTER_W;
  const totalWidth = board.columns.reduce((s, c) => s + c.width, 0) + gutterW;

  // Group headers are drag-reorderable only when grouping by a STATUS column
  // (the order lives in that column's options). Other group-by types derive
  // order from the data and aren't reorderable.
  const groupsDraggable = groupCol?.type === "status" && Boolean(onColumnUpdate);
  const groupItems = grouped
    .filter((g) => g.option && g.option.id !== "__none__")
    .map((g) => g.option!.id);

  // Live cross-group reparent: while a row is dragged over a DIFFERENT group,
  // render it ALREADY moved into that group at the hovered slot, so the
  // destination rows shift to make room (the kanban cross-container preview) and
  // the source group closes its gap. On drop, handleBoardDragEnd persists this.
  const displayGrouped = useMemo<RowGroup[]>(() => {
    if (!activeDragId || !dragOver || groupItems.includes(activeDragId)) return grouped;
    const src = grouped.find((g) => g.rows.some((r) => r.id === activeDragId));
    const dstGroup = grouped.find((g) => g.option?.id === dragOver.groupId);
    if (!src || !dstGroup || src.option?.id === dstGroup.option?.id) return grouped;
    const activeRow = src.rows.find((r) => r.id === activeDragId);
    if (!activeRow) return grouped;
    return grouped.map((g) => {
      if (g.option?.id === src.option?.id) {
        return { ...g, rows: g.rows.filter((r) => r.id !== activeDragId) };
      }
      if (g.option?.id === dragOver.groupId) {
        const rows = g.rows.filter((r) => r.id !== activeDragId);
        const overIdx = rows.findIndex((r) => r.id === dragOver.overId);
        const insertAt = overIdx >= 0 ? overIdx : rows.length;
        const next = [...rows];
        next.splice(insertAt, 0, activeRow);
        return { ...g, rows: next };
      }
      return g;
    });
  }, [grouped, activeDragId, dragOver, groupItems]);
  // Is the row currently being dragged a sub-item? Drives the drop-indicator line
  // (children get the same grip as top-level rows, so the overlay alone can't tell).
  const activeDragRow = activeDragId ? board.rows.find((r) => r.id === activeDragId) : null;
  const activeIsSubitem = Boolean(activeDragRow && activeDragRow.depth > 0);

  // Group-header sortable context (reorder groups). NO own DndContext — the whole
  // board shares ONE DndContext (below) so rows can be dragged ACROSS groups (F5);
  // a per-group context would isolate each group's rows.
  const wrapGroups = (nodes: React.ReactNode) =>
    groupsDraggable ? (
      <SortableContext items={groupItems} strategy={verticalListSortingStrategy}>
        {nodes}
      </SortableContext>
    ) : (
      nodes
    );

  return (
    <div
      style={{ flex: 1, overflow: "auto", position: "relative", outline: "none" }}
      tabIndex={0}
      onKeyDown={onRootKeyDown}
    >
      <div style={{ minWidth: totalWidth }}>
        <div
          style={{
            display: "flex",
            position: "sticky",
            top: 0,
            zIndex: 5,
            background: "var(--surface)",
            borderBottom: "1px solid var(--hairline)",
          }}
        >
          <div
            style={{
              width: gutterW,
              flexShrink: 0,
              borderRight: "1px solid var(--hairline)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 36,
            }}
          >
            <div
              role="checkbox"
              aria-label="Alle auswählen"
              aria-checked={allVisibleSelected ? true : someVisibleSelected ? "mixed" : false}
              title={allVisibleSelected ? "Auswahl aufheben" : "Alle auswählen"}
              onClick={() => setSelected(allVisibleSelected ? new Set() : new Set(flatRowIds))}
              style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  allVisibleSelected || someVisibleSelected ? BRAND.blue : "var(--surface)",
                border: `1.4px solid ${
                  allVisibleSelected || someVisibleSelected ? BRAND.blue : "var(--scale-300)"
                }`,
              }}
            >
              {allVisibleSelected ? (
                <Icon name="check" size={11} color="#fff" stroke={2.5} />
              ) : someVisibleSelected ? (
                <span style={{ width: 8, height: 2, background: "#fff", borderRadius: 1 }} />
              ) : null}
            </div>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleColumnDragEnd}
          >
            <SortableContext
              items={board.columns.map((c) => c.id)}
              strategy={horizontalListSortingStrategy}
            >
              {board.columns.map((c) => (
                <SortableColumnHeader
                  key={c.id}
                  column={c}
                  boardId={board.id as UUID}
                  boardKind={board.kind}
                  canManageOptions={canAddColumnOption}
                  onResize={
                    onColumnUpdate
                      ? (w) =>
                          onColumnUpdate(c.id as UUID, {
                            config: {
                              ...((c.raw?.config as Record<string, unknown>) ?? {}),
                              width: w,
                            },
                          })
                      : undefined
                  }
                  onAutoFit={
                    onColumnUpdate
                      ? () =>
                          onColumnUpdate(c.id as UUID, {
                            config: {
                              ...((c.raw?.config as Record<string, unknown>) ?? {}),
                              width: autoFitColumnWidth(c, board.rows),
                            },
                          })
                      : undefined
                  }
                />
              ))}
            </SortableContext>
          </DndContext>
          <div
            style={{
              width: 44,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {canAddColumn ? (
              <IconButton
                icon="plus"
                size={26}
                title={t.addColumn}
                onClick={() => setAddColOpen(true)}
              />
            ) : null}
          </div>
        </div>

        <AddColumnDialog
          open={addColOpen}
          onOpenChange={setAddColOpen}
          boardId={board.id as UUID}
        />
        {groupDup && onDuplicateGroup && groupByColumnId ? (
          <DuplicateGroupDialog
            open
            onOpenChange={(o) => {
              if (!o) setGroupDup(null);
            }}
            defaultName={`${groupDup.label} Kopie`}
            onConfirm={(name, withComments) => {
              onDuplicateGroup(groupByColumnId, groupDup.sourceValue, name, withComments);
              setGroupDup(null);
            }}
          />
        ) : null}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e: DragStartEvent) => {
            setActiveDragId(String(e.active.id));
            setProjectedDepth(1);
            setSubitemOverId(null);
          }}
          onDragMove={(e: DragMoveEvent) => {
            // Only sub-items project a depth; top-level drags ignore delta.x.
            const active = board.rows.find((r) => r.id === String(e.active.id));
            if (!active || active.depth === 0) return;
            const next = projectDepth(e.delta.x);
            setProjectedDepth((prev) => (prev === next ? prev : next));
          }}
          onDragOver={handleBoardDragOver}
          onDragEnd={handleBoardDragEnd}
          onDragCancel={() => {
            setActiveDragId(null);
            setDragOver(null);
            setProjectedDepth(1);
            setSubitemOverId(null);
          }}
        >
          {wrapGroups(
            displayGrouped.map((g, gi) => {
              const isCollapsed = g.option ? Boolean(collapsed[g.option.id]) : false;
              const groupBudget = g.rows.reduce((s, r) => s + (r.budget ?? 0), 0);
              return (
                <div key={g.option?.id ?? "all"}>
                  {g.option ? (
                    <GroupHeader
                      option={g.option}
                      count={g.rows.length}
                      collapsed={isCollapsed}
                      onToggle={() => toggleCollapsed(g.option!.id)}
                      totalBudget={groupBudget}
                      onDuplicate={
                        onDuplicateGroup && groupByColumnId && g.option.id !== "__none__"
                          ? () =>
                              setGroupDup({
                                sourceValue:
                                  groupCol?.type === "status" ? g.option!.id : g.option!.label,
                                label: g.option!.label,
                              })
                          : undefined
                      }
                      // Category groups on plain project boards only (canDeleteGroup — never
                      // a workflow Status column), and never the "Ohne …" catch-all.
                      onDelete={
                        onDeleteGroup &&
                        groupCol &&
                        canDeleteGroup(board, groupCol) &&
                        g.option.id !== "__none__"
                          ? () =>
                              onDeleteGroup(groupCol.id, {
                                id: g.option!.id,
                                label: g.option!.label,
                              })
                          : undefined
                      }
                      draggable={groupsDraggable && g.option.id !== "__none__"}
                      {...(groupCol?.type === "status" &&
                      onColumnUpdate &&
                      g.option.id !== "__none__"
                        ? {
                            goalDate:
                              groupCol.options?.find((o) => o.id === g.option!.id)?.goal_date ??
                              null,
                            onSetGoalDate: (d: string | null) =>
                              setSectionGoalDate(g.option!.id, d),
                            onRename: (label: string) => renameSection(g.option!.id, label),
                          }
                        : {})}
                    />
                  ) : null}
                  {!isCollapsed ? (
                    <SortableContext
                      // Visible flattened order: each top-level row followed by its
                      // children when expanded. Including child ids is what lets
                      // dnd-kit shift rows to open a gap (make-room) for sub-items too.
                      items={g.rows.flatMap((r) =>
                        expanded.has(r.id) && r.childCount > 0
                          ? [r.id, ...(childrenByParent.get(r.id) ?? []).map((c) => c.id)]
                          : [r.id],
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      {g.rows.map((row) => {
                        const isSel = selected.has(row.id);
                        const isHover = hoverRow === row.id;
                        const isFocused = focusedRowId === row.id;
                        // Require live children: a row whose sub-items were all
                        // deleted can linger in the persisted expanded set, which
                        // otherwise leaves a dangling "+ Untereintrag" footer and
                        // empty children block (the chevron already hides at 0).
                        const isExpanded = expanded.has(row.id) && row.childCount > 0;
                        const children = childrenByParent.get(row.id) ?? [];
                        return (
                          <React.Fragment key={row.id}>
                            <SortableRow
                              row={row}
                              board={board}
                              rowH={rowH}
                              gutterWidth={gutterW}
                              rowGoalDate={resolveRowGoalDate(row)}
                              pillStyle={pillStyle}
                              isSel={isSel}
                              isHover={isHover}
                              isFocused={isFocused}
                              boardHasSubitems={childrenByParent.size > 0}
                              onCopyRow={onCopyRow}
                              groupByColumnId={groupByColumnId}
                              isExpanded={isExpanded}
                              onToggleExpand={
                                row.childCount > 0 ? () => toggleExpand(row.id) : undefined
                              }
                              onAddSubitem={
                                onAddSubitem
                                  ? () => {
                                      if (!isExpanded) toggleExpand(row.id);
                                      onAddSubitem(row.id as UUID);
                                    }
                                  : undefined
                              }
                              onFocus={() => setFocusedRowId(row.id)}
                              onMouseEnter={() => setHoverRow(row.id)}
                              onMouseLeave={() => setHoverRow(null)}
                              onToggleSelect={(shift) => handleSelectClick(row.id, shift)}
                              onPatchRow={bulkOnPatchRow}
                              onRowClick={onRowClick}
                              onAddRow={onAddRow}
                              onPatchCell={bulkOnPatchCell}
                              onAddColumnOption={onAddColumnOption}
                              canAddColumnOption={canAddColumnOption}
                              autoFocus={focusRowId === row.id}
                              isOpen={openRowId === row.id}
                              dropSubitemActive={activeIsSubitem}
                              isSubitemDropTarget={subitemOverId === row.id}
                              subitemProjectedDepth={projectedDepth}
                            />
                            {isExpanded
                              ? children.map((child) => {
                                  const cSel = selected.has(child.id);
                                  const cHover = hoverRow === child.id;
                                  const cFocused = focusedRowId === child.id;
                                  return (
                                    <SortableRow
                                      key={child.id}
                                      row={child}
                                      board={board}
                                      rowH={rowH}
                                      gutterWidth={gutterW}
                                      rowGoalDate={resolveRowGoalDate(child)}
                                      pillStyle={pillStyle}
                                      isSel={cSel}
                                      isHover={cHover}
                                      isFocused={cFocused}
                                      onCopyRow={onCopyRow}
                                      groupByColumnId={groupByColumnId}
                                      // A sub-item's sibling scope is its PARENT — this is
                                      // what Enter in its title cell creates. (No gutter "+"
                                      // appears on it; that is gated on !isSubitem.)
                                      onAddSubitem={
                                        onAddSubitem
                                          ? () => onAddSubitem(row.id as UUID)
                                          : undefined
                                      }
                                      onFocus={() => setFocusedRowId(child.id)}
                                      onMouseEnter={() => setHoverRow(child.id)}
                                      onMouseLeave={() => setHoverRow(null)}
                                      onToggleSelect={(shift) => handleSelectClick(child.id, shift)}
                                      onPatchRow={bulkOnPatchRow}
                                      onRowClick={onRowClick}
                                      onAddRow={onAddRow}
                                      onPatchCell={bulkOnPatchCell}
                                      onAddColumnOption={onAddColumnOption}
                                      canAddColumnOption={canAddColumnOption}
                                      autoFocus={focusRowId === child.id}
                                      isOpen={openRowId === child.id}
                                      dropSubitemActive={activeIsSubitem}
                                      isSubitemDropTarget={subitemOverId === child.id}
                                      subitemProjectedDepth={projectedDepth}
                                      // Move-menu fallback (Task 4): reparent under
                                      // another entry, or promote to top level. Only
                                      // wired when onMoveRow is present (guest → inert).
                                      moveParents={moveParents}
                                      moveSections={moveSections}
                                      onReparentTo={
                                        onMoveRow
                                          ? (parentId: string) =>
                                              onMoveRow(
                                                child.id as UUID,
                                                parentId as UUID,
                                                nextChildPosition(posRows, parentId),
                                              )
                                          : undefined
                                      }
                                      onPromoteTo={
                                        onMoveRow
                                          ? (section: MoveTargetSection | null) => {
                                              onMoveRow(
                                                child.id as UUID,
                                                null,
                                                nextTopLevelPosition(posRows, topLevelIds),
                                              );
                                              if (section && groupCol && onPatchCell) {
                                                // Reuse F5's value encoding.
                                                const value =
                                                  section.value == null
                                                    ? ((groupCol.type === "tags"
                                                        ? []
                                                        : "") as CellValue)
                                                    : (section.value as CellValue);
                                                onPatchCell(child.id, groupCol.id, value);
                                              }
                                            }
                                          : undefined
                                      }
                                    />
                                  );
                                })
                              : null}
                            {isExpanded && onAddSubitem ? (
                              <SubitemAddRow
                                gutterWidth={gutterW}
                                label={board.is_aufgaben ? t.subtaskAdd : t.subitemAdd}
                                onAdd={() => onAddSubitem(row.id as UUID)}
                              />
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </SortableContext>
                  ) : null}
                  {!isCollapsed && onAddRow ? (
                    <AddRowRow
                      rowH={rowH}
                      gutterWidth={gutterW}
                      isLast={gi === grouped.length - 1}
                      onAdd={() => {
                        // B7: add the row INTO this group — set the grouping column's
                        // value so it lands here (not in "Ohne Gruppe"). Status groups
                        // key by option id; generic (text/tags) groups by their label.
                        if (groupCol && g.option && g.option.id !== "__none__") {
                          const value = groupCol.type === "status" ? g.option.id : g.option.label;
                          onAddRow(null, { columnId: groupCol.id as UUID, value });
                        } else {
                          onAddRow(null);
                        }
                      }}
                    />
                  ) : null}
                </div>
              );
            }),
          )}
          <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.2,0,0,1)" }}>
            {activeDragId && !groupItems.includes(activeDragId) ? (
              <DragRowPreview
                title={board.rows.find((r) => r.id === activeDragId)?.title || t.titleEmpty}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {selected.size > 0 ? (
        <BulkActionBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onDelete={onDeleteRow ? () => setConfirmBulkDelete(true) : undefined}
          editableColumns={bulkEditableColumns}
          onBulkSet={onBulkSet}
          workspaceId={board.workspace_id as UUID}
          boardId={board.id as UUID}
          pillStyle={pillStyle}
        />
      ) : null}

      <ConfirmDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        title={t.rowsDeleteTitle}
        message={t.rowsDeleteMessage}
        confirmLabel={t.delete}
        tone="danger"
        onConfirm={() => {
          if (onDeleteRow) {
            // A selected sub-item whose parent is selected too sends no DELETE of its
            // own: the parent's DELETE cascades its live sub-items in the same batch, and
            // the parent's one undo step brings them back. A separate DELETE races the
            // parent's on the board structure lock; when it wins, the sub-item gets its
            // own batch that the parent's undo never restores.
            const parentOf = new Map(board.rows.map((r) => [r.id, r.parentRowId]));
            for (const id of selected) {
              const parentId = parentOf.get(id);
              if (parentId && selected.has(parentId)) continue;
              onDeleteRow(id as UUID);
            }
          }
          setSelected(new Set());
          setConfirmBulkDelete(false);
        }}
      />
    </div>
  );
}

// The display text of one cell for a column (single line — matches the table).
// Compact floating preview of the dragged row (DragOverlay). A condensed card
// (not the full-width row) so the drop animates cleanly across groups.
function DragRowPreview({ title }: { title: string }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        maxWidth: 320,
        padding: "8px 14px",
        background: "var(--surface-elev)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 8,
        boxShadow: "0 12px 30px rgba(13,62,99,0.18), 0 2px 6px rgba(13,62,99,0.08)",
        fontSize: 13,
        fontWeight: 500,
        color: "var(--ink-1)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        cursor: "grabbing",
      }}
    >
      {title}
    </div>
  );
}

function cellText(column: ViewColumn, row: ViewRow): string {
  const v = row.raw?.cells?.find((c) => c.column_def_id === column.id)?.value;
  if (v == null) return "";
  if (column.type === "status") {
    return column.options?.find((o) => o.id === v)?.label ?? (typeof v === "string" ? v : "");
  }
  if (column.type === "tags") return Array.isArray(v) ? v.join(", ") : "";
  if (column.type === "number") return typeof v === "number" ? String(v) : "";
  if (column.type === "link" && v && typeof v === "object") {
    const lv = v as { label?: string; url?: string };
    return String(lv.label ?? lv.url ?? "");
  }
  return typeof v === "string" ? (v.split("\n")[0] ?? "") : "";
}

// Excel-style fit-to-content: widest of the header label + every cell's first
// line, capped. Bound to a double-click on the resize handle.
function autoFitColumnWidth(column: ViewColumn, rows: ViewRow[]): number {
  if (typeof document === "undefined") return column.width;
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return column.width;
  ctx.font = "500 13.5px 'Inter Variable', Inter, system-ui, sans-serif";
  const pillPad = column.type === "status" ? 22 : 0;
  let max = ctx.measureText(column.name).width + 30; // room for the hover menu
  for (const r of rows) {
    const text = cellText(column, r);
    if (text) max = Math.max(max, ctx.measureText(text).width + pillPad);
  }
  return Math.round(Math.max(80, Math.min(800, max + 28)));
}

function SortableColumnHeader({
  column,
  boardId,
  boardKind,
  canManageOptions,
  onResize,
  onAutoFit,
}: {
  column: ViewColumn;
  boardId: UUID;
  boardKind: BoardKind;
  canManageOptions?: boolean;
  onResize?: (newWidth: number) => void;
  onAutoFit?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });
  const [hover, setHover] = useState(false);
  const [draftWidth, setDraftWidth] = useState<number | null>(null);
  const draftWidthRef = useRef<number | null>(null);
  const resizeStartRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Once the column.width prop has caught up to the dragged value (after the
  // optimistic update propagates), clear draftWidth so future external updates
  // (WS from other clients, etc.) flow through normally. Until then, draftWidth
  // keeps the cell at the dragged size — avoids snap-back-to-old-width flicker.
  useEffect(() => {
    if (draftWidth != null && draftWidth === column.width) {
      draftWidthRef.current = null;
      setDraftWidth(null);
    }
  }, [draftWidth, column.width]);

  function onResizeMouseDown(e: React.MouseEvent) {
    if (!onResize) return;
    e.preventDefault();
    e.stopPropagation();
    resizeStartRef.current = { startX: e.clientX, startWidth: column.width };
    draftWidthRef.current = column.width;
    setDraftWidth(column.width);
    function onMove(ev: MouseEvent) {
      if (!resizeStartRef.current) return;
      const dx = ev.clientX - resizeStartRef.current.startX;
      const next = Math.max(80, Math.min(800, resizeStartRef.current.startWidth + dx));
      draftWidthRef.current = next;
      setDraftWidth(next);
    }
    function onUp() {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const finalWidth = draftWidthRef.current;
      resizeStartRef.current = null;
      if (finalWidth != null && finalWidth !== column.width && onResize) {
        onResize(finalWidth);
        // Keep draftWidth visible (set above) until column.width prop catches
        // up. The render-time check above clears it then.
      } else {
        draftWidthRef.current = null;
        setDraftWidth(null);
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  const effectiveWidth = draftWidth ?? column.width;
  // Too narrow to show a readable label → icon only (a truncated "icon + 1 char"
  // reads as broken). The full name stays available via the header tooltip.
  const isNarrow = effectiveWidth < 96;
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      title={isNarrow ? column.name : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: effectiveWidth,
        height: 36,
        padding: "0 12px",
        display: "flex",
        alignItems: "center",
        gap: 6,
        borderRight: "1px solid var(--hairline)",
        background: hover ? "var(--surface-soft)" : "transparent",
        position: "relative",
        flexShrink: 0,
        boxSizing: "border-box",
        cursor: isDragging ? "grabbing" : "grab",
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
    >
      <Icon name={columnIconName(column.type)} size={13} color="var(--ink-3)" stroke={1.8} />
      {isNarrow ? (
        <span style={{ flex: 1 }} />
      ) : (
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            color: "var(--ink-2)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {column.name}
        </span>
      )}
      <ColumnMenu
        boardId={boardId}
        column={column}
        boardKind={boardKind}
        canManageOptions={canManageOptions}
        headerHover={hover}
      />
      {onResize ? (
        <div
          onMouseDown={onResizeMouseDown}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAutoFit?.();
          }}
          style={{
            position: "absolute",
            top: 0,
            right: -2,
            width: 6,
            height: "100%",
            cursor: "col-resize",
            zIndex: 2,
          }}
          title={t.columnResizeHint}
        />
      ) : null}
    </div>
  );
}

interface GroupHeaderProps {
  option: { id: string; label: string; color: string };
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  totalBudget: number;
  /** Vorlaufzeit: this section's Zieltermin + setter (present only when the
   * board has a lead_time column and is grouped by a status column). */
  goalDate?: string | null;
  onSetGoalDate?: (date: string | null) => void;
  /** Rename the section (renames the underlying grouping-column option). */
  onRename?: (label: string) => void;
  /** Duplicate the whole group into a new one (opens the name dialog). */
  onDuplicate?: () => void;
  /** Delete the whole group (the caller asks for confirmation). */
  onDelete?: () => void;
  /** When true the header is drag-sortable (reorders the status options). */
  draggable?: boolean;
}

interface SortableRowProps {
  row: ViewRow;
  board: ViewBoard;
  rowH: number;
  /** Vorlaufzeit: resolved goal date for this row (its section's Zieltermin). */
  rowGoalDate?: string | null;
  /** Shared by every row + the header so columns stay aligned (GUTTER_W / GUTTER_W_TREE). */
  gutterWidth: number;
  pillStyle: PillStyle;
  isSel: boolean;
  isHover: boolean;
  isFocused: boolean;
  /** The board uses sub-items somewhere — enables the category tint on top-level rows. */
  boardHasSubitems?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  /** Create a sub-item in THIS row's sibling scope: under a top-level row (the
   * gutter "+"), or — on a sub-item — under the same parent, so Enter while
   * naming one yields a SIBLING sub-item. The caller binds the parent id. */
  onAddSubitem?: () => void;
  onFocus: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onToggleSelect: (shiftKey: boolean) => void;
  onPatchRow: (id: string, patch: Partial<ViewRow>) => void;
  onRowClick: (row: ViewRow) => void;
  onAddRow?: (afterRowId?: UUID | null) => void;
  onPatchCell?: (rowId: string, columnDefId: string, value: CellValue) => void;
  onAddColumnOption?: (
    column: ColumnDef,
    label: string,
  ) => Promise<{ id: string; label: string; color: string } | null>;
  canAddColumnOption?: boolean;
  /** Just-created row: auto-focus its title cell (cursor) + scroll into view. */
  autoFocus?: boolean;
  /** This row's detail panel is open — persistent brand highlight. */
  isOpen?: boolean;
  // Move-menu fallback (Task 4). Present on sub-items when moves are allowed;
  // absent for guests → the gutter falls back to the inert spacer.
  /** All top-level entries; the sub-item's current parent is filtered out here. */
  moveParents?: MoveTargetParent[];
  /** Group headers to promote into, or null when the board has no group-by. */
  moveSections?: MoveTargetSection[] | null;
  onReparentTo?: (parentId: string) => void;
  onPromoteTo?: (section: MoveTargetSection | null) => void;
  // Task 5 drop indicator (pure visual): a sub-item is being dragged, whether THIS
  // row is the hovered slot, and the binary projected depth for the line's indent.
  dropSubitemActive?: boolean;
  isSubitemDropTarget?: boolean;
  subitemProjectedDepth?: 0 | 1;
  /** Copy menu: bound handler + the active grouping column (for copy-into-group). */
  onCopyRow?: (rowId: UUID, opts: CopyRowOpts) => void;
  groupByColumnId?: string | null;
}

function SortableRow({
  row,
  board,
  rowH,
  rowGoalDate,
  gutterWidth,
  pillStyle,
  isSel,
  isHover,
  isFocused,
  boardHasSubitems = false,
  isExpanded = false,
  autoFocus = false,
  isOpen = false,
  onToggleExpand,
  onAddSubitem,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  onToggleSelect,
  onPatchRow,
  onRowClick,
  onAddRow,
  onPatchCell,
  onAddColumnOption,
  canAddColumnOption,
  moveParents,
  moveSections,
  onReparentTo,
  onPromoteTo,
  dropSubitemActive = false,
  isSubitemDropTarget = false,
  subitemProjectedDepth = 1,
  onCopyRow,
  groupByColumnId = null,
}: SortableRowProps) {
  const isSubitem = row.depth > 0;
  // A "category" = any top-level row, on a board that uses sub-items. Every category
  // gets a faint resting tint (var(--row-category-tint), theme-flipping so it's visible
  // in dark mode too) so the level reads clearly — including a collapsed category, where
  // its sub-items aren't on screen. Flat boards (no sub-items) get no tint.
  const isCategory = row.depth === 0 && boardHasSubitems;
  // A sub-item is draggable only when the move capability is wired (the same gate
  // the move menu below uses). A guest — for whom onMoveRow, and therefore these
  // callbacks, is absent — gets an inert sub-item rather than a drag the backend
  // would only 403 + roll back. Top-level rows are unaffected (never gated here).
  const canMoveSubitem = Boolean(onReparentTo && onPromoteTo);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: isSubitem && !canMoveSubitem,
  });
  // Draw the drop-indicator line at this slot only while a sub-item is dragged
  // over it (and never on the dragged row itself).
  const showDropLine = dropSubitemActive && isSubitemDropTarget && !isDragging;
  // Right-click copy menu, positioned at the cursor. Kept out of the row gutter (an
  // extra gutter control there had pushed the selection checkbox out of reach).
  const [copyMenu, setCopyMenu] = useState<{ x: number; y: number } | null>(null);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      // Deep-link anchor (jump-to-comment ① / bell): the open row scrolls itself
      // into view via this id (see the openRowId scroll effect in TableView).
      id={`board-row-${row.id}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Multi-select modifiers (Gmail/Finder style), captured so they win over the
      // title cell's click-to-open-detail. Shift+click = contiguous range from the
      // anchor; Ctrl/Cmd+click = toggle this single row (non-contiguous). preventDefault
      // on mousedown stops the browser's native text highlight while shift is held.
      onMouseDown={(e) => {
        if (e.shiftKey) e.preventDefault();
        onFocus();
      }}
      onClickCapture={(e) => {
        if (e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          onToggleSelect(true);
        } else if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          e.stopPropagation();
          onToggleSelect(false);
        }
      }}
      onContextMenu={
        onCopyRow
          ? (e) => {
              e.preventDefault();
              setCopyMenu({ x: e.clientX, y: e.clientY });
            }
          : undefined
      }
      style={{
        display: "flex",
        borderBottom: "1px solid var(--hairline-soft)",
        // Priority: selection > open-detail > keyboard-focus > hover. The open
        // row keeps a persistent tint (deepening slightly on hover, so hover
        // still "works together") + a 3px brand accent, distinct from the 2px
        // keyboard-focus accent — so a bell deep-link makes its row obvious.
        background: isSel
          ? "rgba(0, 89, 155, 0.06)"
          : isOpen
            ? isHover
              ? "rgba(0, 89, 155, 0.11)"
              : "rgba(0, 89, 155, 0.08)"
            : isFocused
              ? "rgba(0, 89, 155, 0.04)"
              : isHover
                ? "var(--surface-soft)"
                : isCategory
                  ? "var(--row-category-tint)"
                  : "var(--surface)",
        position: "relative",
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
        boxShadow: isOpen
          ? `inset 3px 0 0 ${BRAND.blue}`
          : isFocused && !isSel
            ? `inset 2px 0 0 ${BRAND.blue}`
            : "none",
      }}
    >
      {showDropLine ? (
        // Thin slot line for the sub-item drag, indented by the projected depth
        // (0 = promote to top-level, 1 = nest). Pure visual — the make-room shift
        // is native to the SortableContext.
        <div
          style={{
            position: "absolute",
            left: subitemProjectedDepth ? 24 : 0,
            right: 0,
            top: -1,
            height: 2,
            background: "var(--accent)",
            zIndex: 12,
            pointerEvents: "none",
          }}
        />
      ) : null}
      {isSel ? (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 2,
            background: BRAND.blue,
          }}
        />
      ) : null}
      {copyMenu && onCopyRow ? (
        <RowContextMenu
          x={copyMenu.x}
          y={copyMenu.y}
          isSubitem={isSubitem}
          parents={(moveParents ?? []).filter((p) => p.id !== row.parentRowId)}
          sections={moveSections ?? null}
          groupColumnId={groupByColumnId ? (groupByColumnId as UUID) : null}
          onCopy={(opts) => onCopyRow(row.id as UUID, opts)}
          onClose={() => setCopyMenu(null)}
        />
      ) : null}
      {/* Leading indent for a sub-item: shifts the whole gutter (drag grip + "Verschieben
          nach" menu) right so the HANDLES read as indented too, not just the title. The
          title cell is narrowed by the same amount below, so every later column stays
          aligned — and the gutter's own contents (checkbox included) are untouched, so
          this can't crowd the selection checkbox out. */}
      {isSubitem ? <div style={{ width: 32 * row.depth, flexShrink: 0 }} /> : null}
      <div
        style={{
          width: gutterWidth,
          flexShrink: 0,
          borderRight: isSubitem ? "none" : "1px solid var(--hairline-soft)",
          display: "flex",
          alignItems: "center",
          gap: GUTTER_GAP,
          padding: `0 ${GUTTER_PAD}px`,
          height: rowH,
        }}
      >
        {/* Expand chevron (parents), then the drag grip. The chevron is always
            visible so users can find their sub-items; the grip is resting-visible
            (calm) and lifts on hover. */}
        {onToggleExpand ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            // Toggling sub-items must NOT focus the row: the row focuses on mousedown,
            // and a click bubbles a mousedown up here — which left the row stuck with a
            // focus accent bar (a dark-blue band in dark mode) until focus moved away.
            onMouseDown={(e) => e.stopPropagation()}
            role="button"
            tabIndex={0}
            title={isExpanded ? "Untereinträge ausblenden" : "Untereinträge einblenden"}
            style={{
              width: 14,
              height: 14,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "var(--ink-3)",
              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform .12s",
            }}
          >
            <Icon name="chevRight" size={12} />
          </div>
        ) : null}
        {/* Drag grip — the primary reorder/reparent/promote handle for BOTH
            top-level rows AND sub-items (Task 5 made sub-items draggable, so they
            get the same grip instead of the old spacer). A sub-item without the
            move capability (guest) renders an inert spacer instead, matching the
            hidden move menu — the grip and the menu appear together or not at all. */}
        {!isSubitem || canMoveSubitem ? (
          <div
            {...listeners}
            style={{
              width: 16,
              height: 18,
              flexShrink: 0,
              borderRadius: 4,
              // Resting-VISIBLE (was opacity:0 until hover, so users couldn't
              // find the grip) at a calm strength, lifting to a clear chip on
              // hover so it reads as a grabbable handle.
              opacity: isHover ? 1 : 0.7,
              color: isHover ? "var(--ink-1)" : "var(--ink-2)",
              background: isHover ? "var(--surface-soft)" : "transparent",
              transition: "opacity .1s, color .1s, background .1s",
              cursor: isDragging ? "grabbing" : "grab",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Zeile verschieben"
          >
            <Icon name="drag" size={16} stroke={2.8} />
          </div>
        ) : (
          <div style={{ width: 16, height: 18, flexShrink: 0 }} />
        )}
        {/* Sub-item move menu (Task 4): a keyboard/touch-friendly path that
            coexists with the drag grip. Resting-muted, full on row-hover. */}
        {isSubitem && onReparentTo && onPromoteTo ? (
          <div
            style={{
              flexShrink: 0,
              opacity: isHover ? 1 : 0.45,
              transition: "opacity .1s",
              display: "flex",
              alignItems: "center",
            }}
          >
            <MoveSubitemMenu
              parents={(moveParents ?? []).filter((p) => p.id !== row.parentRowId)}
              sections={moveSections ?? null}
              onReparent={onReparentTo}
              onPromote={onPromoteTo}
            />
          </div>
        ) : null}
        {/* "Details öffnen" gutter arrow removed — the title cell (click empty
            space) and Space/Enter already open the panel; this decluttered the
            cramped gutter (chevron + select + add-subitem). */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(e.shiftKey);
          }}
          role="checkbox"
          aria-checked={isSel}
          style={{
            width: 16,
            height: 16,
            // Load-bearing: unchecked it has no content, so without this flex-shrink
            // takes it down to its borders the moment the gutter gets tight.
            flexShrink: 0,
            borderRadius: 4,
            cursor: "pointer",
            opacity: isHover || isSel ? 1 : 0,
            transition: "opacity .1s",
            background: isSel ? BRAND.blue : "var(--surface)",
            border: `1.4px solid ${isSel ? BRAND.blue : "var(--scale-300)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSel ? <Icon name="check" size={11} color="#fff" stroke={2.5} /> : null}
        </div>
        {/* + Untereintrag for top-level rows. Hidden for sub-items since v1 caps
            nesting at depth=1.

            🔴 RESTING-VISIBLE, not hover-only. This was a 14px button at opacity 0
            until you happened to hover the gutter — so sub-items were a feature
            nobody could find, and colleagues asked for a capability the board had
            all along. That is the THIRD time this exact bug has been reported here:
            the column "⋯" menu and the row drag handles were both hover-only-and-
            faint and both had to be made resting-visible after the same feedback.
            Muted at rest so a long board stays calm; full strength on row-hover. */}
        {!isSubitem && onAddSubitem ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onAddSubitem();
            }}
            role="button"
            tabIndex={0}
            // On an Aufgaben board a sub-row IS an Aufgabe, not an "Eintrag" — the generic
            // board vocabulary lies about what the row is (cf. 09a0c02, "das Ticket" /
            // "den Eintrag" in the activity feed).
            title={board.is_aufgaben ? t.subtaskAdd : t.subitemAdd}
            style={{
              width: 16,
              height: 16,
              flexShrink: 0,
              opacity: isHover ? 1 : 0.4,
              transition: "opacity .1s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: 4,
              color: isHover ? "var(--ink-2)" : "var(--ink-3)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = BRAND.orange)}
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = isHover ? "var(--ink-2)" : "var(--ink-3)")
            }
          >
            <Icon name="plus" size={13} stroke={2.25} />
          </div>
        ) : null}
      </div>
      {board.columns.map((c, ci) => (
        <div
          key={c.id}
          style={{
            // Sub-item title cell is narrowed by the leading indent (the spacer added
            // before the gutter), so the title text keeps its position AND every later
            // column stays aligned with its header.
            width: ci === 0 && isSubitem ? c.width - 32 * row.depth : c.width,
            flexShrink: 0,
            borderRight: "1px solid var(--hairline-soft)",
            boxSizing: "border-box",
          }}
        >
          <Cell
            column={c}
            row={row}
            rowH={rowH}
            pillStyle={pillStyle}
            sticky={ci === 0}
            autoFocus={autoFocus && ci === 0}
            workspaceId={board.workspace_id}
            boardKind={board.kind}
            boardGoalDate={rowGoalDate ?? null}
            boardId={board.id}
            ticketPrefix={board.ticketPrefix}
            onPatchRow={onPatchRow}
            onRowClick={onRowClick}
            // 🔴 Enter in a sub-item's title COMMITS AND CLOSES — it must never mint a
            // next row. The spreadsheet convention (Enter = start typing the next one)
            // leaves a pristine empty row behind the moment the user stops, and an empty
            // sub-task is not harmless here: it counts toward the Protokoll's
            // "N von M erledigt" rollup, so the minutes report a task that does not exist.
            // Two earlier fixes only moved that stray row around (top-level → sibling)
            // instead of removing it. Add further sub-tasks via the gutter "+", the
            // footer, or the detail panel's create box — none of which pre-mint a row.
            onCreateNext={isSubitem ? undefined : onAddRow}
            onPatchCell={onPatchCell}
            onAddColumnOption={onAddColumnOption}
            canAddColumnOption={canAddColumnOption}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * The trailing add-affordance under an expanded row's children.
 *
 * 🔴 NOT a row. It used to be one in everything but name: full-width flex strip, its own
 * `borderBottom` hairline, row-height — so clicking the gutter "+" looked like it created TWO
 * rows ("when I clicked the + it creates the new row (ok) but ALSO a row for '+ Untereintrag
 * hinzufügen' below"). It is now an indented, plain inline text button in the title column
 * only: no background, no border, `width: fit-content` so it neither spans the grid nor
 * hit-tests across it. Nothing about it reads as data.
 */
function SubitemAddRow({
  gutterWidth,
  label,
  onAdd,
}: {
  gutterWidth: number;
  label: string;
  onAdd: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    // Aligned under the sub-item titles: gutter + the 32px sub-item indent + 4px.
    <div style={{ width: "fit-content", padding: `3px 0 5px ${gutterWidth + 36}px` }}>
      <button
        type="button"
        onClick={onAdd}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "2px 6px",
          margin: 0,
          border: "none",
          background: "transparent",
          borderRadius: 4,
          font: "inherit",
          fontSize: 12.5,
          lineHeight: 1.3,
          color: hover ? BRAND.orange : "var(--ink-4)",
          cursor: "pointer",
        }}
      >
        <Icon name="plus" size={12} /> {label}
      </button>
    </div>
  );
}

function GroupHeader({
  option,
  count,
  collapsed,
  onToggle,
  totalBudget,
  goalDate,
  onSetGoalDate,
  onRename,
  onDuplicate,
  onDelete,
  draggable = false,
}: GroupHeaderProps) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: option.id,
    disabled: !draggable,
  });
  const tint = statusColorOf(option.color);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(option.label);
  const clickTimer = useRef<number | null>(null);

  function commitRename() {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== option.label) onRename?.(next);
  }
  // Single click toggles (briefly delayed so a double-click can cancel it);
  // double-click on the label opens inline rename. Avoids the toggle flicker.
  function handleLabelClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (clickTimer.current != null) {
      window.clearTimeout(clickTimer.current);
      clickTimer.current = null;
      return;
    }
    clickTimer.current = window.setTimeout(() => {
      clickTimer.current = null;
      onToggle();
    }, 220);
  }
  function handleLabelDoubleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (clickTimer.current != null) {
      window.clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    setDraft(option.label);
    setEditing(true);
  }
  return (
    <div
      ref={setNodeRef}
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px 8px 12px",
        cursor: "pointer",
        borderBottom: "1px solid var(--hairline-soft)",
        background: "var(--surface)",
        position: "sticky",
        top: 36,
        zIndex: isDragging ? 6 : 4,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      {draggable ? (
        <span
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          title="Gruppe verschieben"
          style={{ display: "inline-flex", cursor: "grab", color: "var(--ink-2)", marginLeft: -4 }}
        >
          <Icon name="drag" size={16} stroke={2.8} />
        </span>
      ) : null}
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          background: tint.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: collapsed ? "rotate(-90deg)" : "rotate(0)",
          transition: "transform .15s",
        }}
      >
        <Icon name="chevDown" size={10} color={tint.fg} stroke={2.2} />
      </div>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitRename();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(option.label);
              setEditing(false);
            }
          }}
          onBlur={commitRename}
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.01em",
            border: "1px solid var(--hairline)",
            borderRadius: 6,
            padding: "2px 6px",
            background: "var(--surface)",
            outline: "none",
            minWidth: 0,
          }}
        />
      ) : (
        <span
          onClick={onRename ? handleLabelClick : undefined}
          onDoubleClick={onRename ? handleLabelDoubleClick : undefined}
          title={onRename ? "Doppelklick zum Umbenennen" : undefined}
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          {option.label}
        </span>
      )}
      {onSetGoalDate ? <GoalDateControl value={goalDate ?? null} onChange={onSetGoalDate} /> : null}
      <span
        style={{
          fontSize: 12,
          color: "var(--ink-3)",
          fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
        }}
      >
        {count}
      </span>
      <div style={{ flex: 1 }}></div>
      {totalBudget > 0 ? (
        <span
          style={{
            fontSize: 12,
            color: "var(--ink-3)",
            fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
          }}
        >
          {fmtCurrency(totalBudget)}
        </span>
      ) : null}
      {onDuplicate ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          title={t.groupDuplicateTitle}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink-1)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-3)")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            border: "none",
            background: "transparent",
            color: "var(--ink-3)",
            cursor: "pointer",
            padding: "2px 4px",
            borderRadius: 4,
          }}
        >
          <Icon name="copy" size={13} />
        </button>
      ) : null}
      {onDelete ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title={t.groupDeleteTitle}
          aria-label={t.groupDeleteTitle}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--danger)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-3)")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            border: "none",
            background: "transparent",
            color: "var(--ink-3)",
            cursor: "pointer",
            padding: "2px 4px",
            borderRadius: 4,
          }}
        >
          <Icon name="trash" size={13} />
        </button>
      ) : null}
    </div>
  );
}

function AddRowRow({
  rowH,
  gutterWidth,
  isLast,
  onAdd,
}: {
  rowH: number;
  gutterWidth: number;
  isLast: boolean;
  onAdd?: () => void;
}) {
  return (
    <div
      role={onAdd ? "button" : undefined}
      tabIndex={onAdd ? 0 : undefined}
      onClick={onAdd}
      onKeyDown={
        onAdd
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onAdd();
              }
            }
          : undefined
      }
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        // Aligned with the title text: gutter + the title cell's 12px padding.
        padding: `0 12px 0 ${gutterWidth + 12}px`,
        height: rowH,
        color: "var(--ink-3)",
        fontSize: 13,
        cursor: onAdd ? "pointer" : "default",
        borderBottom: isLast ? "none" : "1px solid var(--hairline-soft)",
        opacity: onAdd ? 1 : 0.6,
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (onAdd) e.currentTarget.style.color = BRAND.orange;
      }}
      onMouseLeave={(e) => {
        if (onAdd) e.currentTarget.style.color = "var(--ink-3)";
      }}
    >
      <Icon name="plus" size={14} /> {t.addRow}
    </div>
  );
}

function BulkActionBar({
  count,
  onClear,
  onDelete,
  editableColumns = [],
  onBulkSet,
  workspaceId,
  boardId,
  pillStyle,
}: {
  count: number;
  onClear: () => void;
  onDelete?: () => void;
  editableColumns?: BulkEditableColumn[];
  onBulkSet?: (columnId: string, value: CellValue) => void;
  workspaceId: UUID | null;
  boardId: UUID | null;
  pillStyle: PillStyle;
}) {
  const btn = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    border: "none",
    color: "#fff",
    fontSize: 12.5,
    padding: "4px 8px",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  } as const;
  return (
    <div
      style={{
        position: "absolute",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        background: SCALE[800],
        color: "#fff",
        padding: "8px 8px 8px 14px",
        borderRadius: 10,
        boxShadow: "0 10px 30px rgba(13,62,99,0.25)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: 13,
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        zIndex: 30,
      }}
    >
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {count} {t.selected}
      </span>
      <span style={{ width: 1, height: 18, background: "rgba(255,255,255,0.15)" }}></span>
      {editableColumns.length > 0 && onBulkSet ? (
        <BulkFieldMenu
          columns={editableColumns}
          workspaceId={workspaceId}
          boardId={boardId}
          pillStyle={pillStyle}
          buttonStyle={btn}
          onApply={onBulkSet}
        />
      ) : null}
      <button style={btn} onClick={onDelete} disabled={!onDelete}>
        <Icon name="trash" size={13} /> {t.delete}
      </button>
      <button onClick={onClear} style={{ ...btn, marginLeft: 4, color: "rgba(255,255,255,0.6)" }}>
        <Icon name="x" size={13} />
      </button>
    </div>
  );
}
